/* ═══════════════════════════════════════════════════════════════
   INSERTING A MODULE into a document — the editor half of the shared
   brain (docs/MASTERS_UNIFIED_PLAN.md Phase 5).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a
   variant (a variant is a client version of a master).

   ⛔ STAMPED COPY LAW (v2.12 grave): what lands here is a COPY, tagged
   with the module's identity and version. Nothing in this file — or
   anywhere downstream — ever resolves that stamp back to the library to
   re-read content. The blocks in the document are the whole truth.

   This lives in `designer/` on purpose: it is the layer BOTH editors
   stand on. The Spec Masters Studio (masters + client variants) and the
   Designer (project specs) each wire their own panel and menu to these
   same transactions, so an insertion behaves identically in all three
   places and there is no second implementation to drift.

   Everything is a plain function over EditorState — one transaction per
   action, so one Ctrl+Z puts the document back exactly as it was.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { ContentBlock } from '../../shared/types';
import type { ModuleStamp } from '../../shared/spec-modules';
import { insertionColorSlots } from '../../shared/spec-modules';
import { snapBoundaryOutOfGroup } from '../../shared/block-groups';
import type { VariantChange } from '../../shared/variant-updates';
import { BLOCK_NODE_NAMES, groupIdOf } from './commands';
import { blocksToDoc, newBlockId } from './schema';

type Dispatch = ((tr: Transaction) => void) | undefined;

export interface TopBlock {
  id: string;
  node: PMNode;
  pos: number;
  stamp: ModuleStamp | null;
}

/** The stamp a node carries, if an insertion put it there. It rides in the
 *  node's `meta` attribute — the schema's catch-all for every ContentBlock
 *  field it does not model explicitly — so it survives every save and reload
 *  with no attribute of its own (proved in schema.test.ts). */
export function stampOfNode(node: PMNode): ModuleStamp | null {
  const meta = node.attrs?.meta as Record<string, unknown> | undefined;
  const stamp = meta?.moduleStamp as ModuleStamp | undefined;
  return stamp && stamp.insertId ? stamp : null;
}

/** Every top-level block, in document order, with its stamp already read. */
export function topBlocks(state: EditorState): TopBlock[] {
  const out: TopBlock[] = [];
  state.doc.forEach((node, pos) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    out.push({
      id: (node.attrs.blockId as string) || '',
      node,
      pos,
      stamp: stampOfNode(node),
    });
  });
  return out;
}

/** The stamp on one block, by id — what the right-click menu reads. */
export function stampOfBlock(state: EditorState, blockId: string): ModuleStamp | null {
  return topBlocks(state).find((b) => b.id === blockId)?.stamp ?? null;
}

/** Every block of one insertion, in document order. */
export function insertionBlockIds(state: EditorState, insertId: string): string[] {
  return topBlocks(state)
    .filter((b) => b.stamp?.insertId === insertId)
    .map((b) => b.id)
    .filter(Boolean);
}

/** The contiguous run of one insertion that starts at (or contains)
 *  `aroundBlockId` — one PIECE as its name tag names it. */
export function insertionRunIds(
  state: EditorState,
  insertId: string,
  aroundBlockId: string
): string[] {
  const all = topBlocks(state);
  const mine = (b: TopBlock) => b.stamp?.insertId === insertId;
  let start = all.findIndex((b) => b.id === aroundBlockId && mine(b));
  if (start < 0) start = all.findIndex(mine);
  if (start < 0) return [];
  let end = start;
  while (start > 0 && mine(all[start - 1])) start--;
  while (end < all.length - 1 && mine(all[end + 1])) end++;
  return all.slice(start, end + 1).map((b) => b.id);
}

/**
 * One piece and the BOUNDARIES it may be dragged between: the gaps inside
 * its own Part — after the Part's heading (or the top of the document) and
 * up to the next Part heading (or the end). A piece moves within its Part;
 * crossing into another Part is a different placement and stays Insert's job.
 */
export function insertionPiece(
  state: EditorState,
  insertId: string,
  aroundBlockId: string
): { ids: string[]; bounds: [number, number] } | null {
  const ids = insertionRunIds(state, insertId, aroundBlockId);
  if (!ids.length) return null;
  const all = topBlocks(state);
  const first = all.findIndex((b) => b.id === ids[0]);
  const last = first + ids.length - 1;
  const isPart = (b: TopBlock) => Number(b.node.attrs.level || 1) === 1;
  let lo = 0;
  for (let i = first - 1; i >= 0; i--) {
    if (isPart(all[i])) {
      lo = i + 1;
      break;
    }
  }
  let hi = all.length;
  for (let i = last + 1; i < all.length; i++) {
    if (isPart(all[i])) {
      hi = i;
      break;
    }
  }
  return { ids, bounds: [lo, hi] };
}

/* ─── Landing a module ─── */

/** One chunk on its way in: the stamped blocks, and where they go. Exactly
 *  ONE of `afterBlockId` / `atIndex` is meaningful; `atIndex` is a BOUNDARY
 *  index (the gap before block N, `length` meaning the end of the document),
 *  which is what a click-to-place answers with. */
export interface ModuleDrop {
  blocks: ContentBlock[];
  /** Put them straight after this block (what `matchAnchor` answers). */
  afterBlockId?: string | null;
  /** …or at this gap (what placing a chip by hand answers). */
  atIndex?: number | null;
  /** AUTOMATIC placement (anchor match, click-to-place, a toolbox drop): a
   *  landing that would fall BETWEEN two blocks of one group is pushed to just
   *  after that group — groups are solid (modules-polish, item 2). Absent on
   *  the library-update path, whose added paragraph belongs beside its own
   *  neighbours wherever they stand. */
  outsideGroups?: boolean;
}

/** The group-boundary rows of the document, in block order. */
export function groupRows(state: EditorState): Array<{ id: string; group: string }> {
  return topBlocks(state).map((b) => ({ id: b.id, group: groupIdOf(b.node) }));
}

/** A boundary index pulled clear of any group it would split — `nearest` for
 *  a pointer, `after` for an automatic landing (shared/block-groups.ts). */
export function snapBoundary(
  state: EditorState,
  index: number,
  prefer: 'nearest' | 'after' = 'nearest'
): number {
  return snapBoundaryOutOfGroup(groupRows(state), index, { prefer });
}

/** ContentBlock[] → real nodes for THIS editor's schema. */
function nodesFor(state: EditorState, blocks: ContentBlock[]): PMNode[] {
  if (!blocks.length) return [];
  const json = blocksToDoc(blocks).content || [];
  return json.map((node) => state.schema.nodeFromJSON(node));
}

function landingPos(state: EditorState, all: TopBlock[], drop: ModuleDrop): number {
  if (drop.outsideGroups) {
    // Resolve to a BOUNDARY first so the group rule can read it; only when the
    // rule actually moves the landing does the position differ from the plain
    // path below.
    const at = drop.afterBlockId
      ? all.findIndex((b) => b.id === drop.afterBlockId) + 1
      : drop.atIndex != null
        ? Math.max(0, Math.min(drop.atIndex, all.length))
        : -1;
    if (at > 0) {
      const rows = all.map((b) => ({ id: b.id, group: groupIdOf(b.node) }));
      const snapped = snapBoundaryOutOfGroup(rows, at, { prefer: 'after' });
      if (snapped !== at) {
        return snapped < all.length ? all[snapped].pos : state.doc.content.size;
      }
    }
  }
  if (drop.afterBlockId) {
    const target = all.find((b) => b.id === drop.afterBlockId);
    if (target) return target.pos + target.node.nodeSize;
    // The anchor block left the document between the match and the insert:
    // the end of the document is the honest fallback — never a wrong guess
    // about which block "meant" to be the anchor.
    return state.doc.content.size;
  }
  if (drop.atIndex != null) {
    const index = Math.max(0, Math.min(drop.atIndex, all.length));
    return index < all.length ? all[index].pos : state.doc.content.size;
  }
  return state.doc.content.size;
}

/**
 * Insert one or more chunks in ONE transaction, so a whole module insert is
 * ONE Ctrl+Z. Every landing position is computed against the document as it
 * stands BEFORE anything moves, then applied LAST-first — the same rule the
 * gutter's multi-block move stands on, and what makes a two-chunk module land
 * in two different articles without the first insert shifting the second.
 *
 * The caret follows the FIRST chunk in document order (which is inserted last,
 * so its position is still valid) and the sheet scrolls to it — the user sees
 * what landed and where.
 */
export function insertModuleDrops(
  state: EditorState,
  dispatch: Dispatch,
  drops: ModuleDrop[]
): boolean {
  const real = drops.filter((d) => d.blocks && d.blocks.length);
  if (!real.length) return false;
  const all = topBlocks(state);

  const planned = real
    .map((drop, order) => ({
      pos: landingPos(state, all, drop),
      nodes: nodesFor(state, drop.blocks),
      order,
    }))
    .filter((p) => p.nodes.length)
    // Last position first so earlier ones stay valid; drops sharing a position
    // go last-authored first so each insert pushes the earlier one down and
    // the authored order survives.
    .sort((a, b) => b.pos - a.pos || b.order - a.order);
  if (!planned.length) return false;

  const tr = state.tr;
  for (const p of planned) tr.insert(p.pos, p.nodes);

  const first = planned[planned.length - 1].pos;
  const caret = Math.min(first + 1, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Delete every block of one insertion — the whole unit, wherever its chunks
 * ended up, one transaction, one Ctrl+Z. Ranges go LAST first so the earlier
 * positions stay valid (an insertion can be non-contiguous). A document that
 * would be left with nothing keeps one empty paragraph, because a spec always
 * has somewhere to put the caret.
 */
export function removeInsertion(state: EditorState, dispatch: Dispatch, insertId: string): boolean {
  const all = topBlocks(state);
  const doomed = all.filter((b) => b.stamp?.insertId === insertId);
  if (!doomed.length) return false;

  const tr = state.tr;
  for (let i = doomed.length - 1; i >= 0; i--) {
    tr.delete(doomed[i].pos, doomed[i].pos + doomed[i].node.nodeSize);
  }
  if (doomed.length === all.length) {
    tr.insert(
      0,
      state.schema.nodes.specParagraph.create({
        blockId: newBlockId(),
        level: 1,
        continuation: false,
        excluded: false,
        meta: {},
      })
    );
  }
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Select an insertion where there is no gutter selection layer to hand (the
 * Designer): a text selection over the CONTIGUOUS run that contains
 * `aroundBlockId`. Answers how many of the insertion's blocks that run covers,
 * so the caller can say so plainly when a multi-chunk insertion sits in more
 * than one place — a selection that silently swallowed the untouched blocks
 * between two chunks would be a lie the Delete key could act on.
 */
export function selectInsertionRun(
  state: EditorState,
  dispatch: Dispatch,
  insertId: string,
  aroundBlockId: string
): { selected: number; total: number } | null {
  const all = topBlocks(state);
  const mine = (b: TopBlock) => b.stamp?.insertId === insertId;
  const total = all.filter(mine).length;
  if (!total) return null;

  let start = all.findIndex((b) => b.id === aroundBlockId && mine(b));
  if (start < 0) start = all.findIndex(mine);
  let end = start;
  while (start > 0 && mine(all[start - 1])) start--;
  while (end < all.length - 1 && mine(all[end + 1])) end++;

  const from = all[start].pos;
  const to = all[end].pos + all[end].node.nodeSize;
  // `between` snaps each end to the nearest position that can hold a text
  // selection, so a run that starts or ends on a table or a figure selects
  // cleanly instead of throwing.
  const tr = state.tr.setSelection(
    TextSelection.between(state.doc.resolve(from), state.doc.resolve(to))
  );
  if (dispatch) dispatch(tr.scrollIntoView());
  return { selected: end - start + 1, total };
}

/* ─── Answering a library update, in the document (R2 Phase 3) ───
   The decision itself is made in `shared/module-updates.ts`: every change
   arrives carrying the exact block to land, stamp included. This is only the
   ProseMirror flavour of "put it there" — one transaction per answer, so one
   Ctrl+Z takes it back, exactly like the paragraph edit it stands in for. */

/** Swap one block's node for another block, keeping its place. */
function replaceBlockNode(
  state: EditorState,
  tr: Transaction,
  blockId: string,
  block: ContentBlock
): boolean {
  const target = topBlocks(state).find((b) => b.id === blockId);
  const node = nodesFor(state, [block])[0];
  if (!target || !node) return false;
  tr.replaceWith(target.pos, target.pos + target.node.nodeSize, node);
  return true;
}

/**
 * One accepted change from a LOCAL module update, applied to the document.
 * A rejected change writes nothing — that is the whole point of rejecting.
 */
export function applyLocalModuleChange(
  state: EditorState,
  dispatch: Dispatch,
  change: VariantChange,
  decision: 'accepted' | 'rejected'
): boolean {
  if (decision === 'rejected') return false;

  if (change.kind === 'removed') {
    const target = topBlocks(state).find((b) => b.id === change.blockId);
    if (!target) return false;
    const tr = state.tr.delete(target.pos, target.pos + target.node.nodeSize);
    if (dispatch) dispatch(tr.scrollIntoView());
    return true;
  }

  const block = change.afterBlock;
  if (!block) return false;

  if (change.kind === 'modified') {
    const tr = state.tr;
    if (!replaceBlockNode(state, tr, change.blockId, block)) return false;
    if (dispatch) dispatch(tr.scrollIntoView());
    return true;
  }

  // added — beside the neighbours the change names, never at the end of the
  // document (`insertModuleDrops` owns the geometry).
  const all = topBlocks(state);
  if (all.some((b) => b.id === block.id)) return false;
  if (change.anchorAfterId && all.some((b) => b.id === change.anchorAfterId)) {
    return insertModuleDrops(state, dispatch, [
      { blocks: [block], afterBlockId: change.anchorAfterId },
    ]);
  }
  if (change.anchorBeforeId) {
    const at = all.findIndex((b) => b.id === change.anchorBeforeId);
    if (at >= 0) return insertModuleDrops(state, dispatch, [{ blocks: [block], atIndex: at }]);
  }
  // The anchor is gone: the end of THIS insertion is the honest spot.
  let last = -1;
  all.forEach((b, i) => {
    if (b.stamp?.insertId === change.insertId) last = i;
  });
  return insertModuleDrops(state, dispatch, [
    { blocks: [block], ...(last >= 0 ? { atIndex: last + 1 } : {}) },
  ]);
}

/**
 * Move part of the stamp on every block of one insertion — what closing out
 * an update ("this copy has now answered v3") and "Keep all mine" both write.
 * One transaction; the document is dirty afterwards, like any edit.
 */
export function restampInsertionNodes(
  state: EditorState,
  dispatch: Dispatch,
  insertId: string,
  patch: Partial<ModuleStamp>,
  /** Extra, per block: what a knowingly-rejected paragraph records about
   *  itself (`localDeviationPatches`). */
  perBlock: Record<string, Partial<ModuleStamp>> = {}
): boolean {
  const mine = topBlocks(state).filter((b) => b.stamp?.insertId === insertId);
  if (!mine.length) return false;
  const tr = state.tr;
  for (const block of mine) {
    const meta = (block.node.attrs.meta as Record<string, unknown>) || {};
    tr.setNodeMarkup(block.pos, undefined, {
      ...block.node.attrs,
      meta: {
        ...meta,
        moduleStamp: {
          ...(block.stamp as ModuleStamp),
          ...patch,
          ...(perBlock[block.id] || {}),
        },
      },
    });
  }
  if (dispatch) dispatch(tr);
  return true;
}

/* ─── Placing a chunk by hand ─── */

/**
 * The BOUNDARY index a pointer is over: the gap before block N, `length`
 * meaning the end of the document. Decided by the rendered rows' vertical
 * midpoints — the same geometry the gutter's drag line uses, so click-to-place
 * and drag-to-move can never disagree about where "here" is.
 */
export function boundaryIndexAtCoords(view: EditorView, clientY: number): number {
  const all = topBlocks(view.state);
  for (let i = 0; i < all.length; i++) {
    const dom = view.nodeDOM(all[i].pos) as HTMLElement | null;
    const rect = dom?.getBoundingClientRect?.();
    if (!rect) continue;
    if (clientY < rect.top + rect.height / 2) return i;
  }
  return all.length;
}

/** Where the drop line should be drawn for boundary index `index`, in viewport
 *  coordinates — null when the document has no rendered rows yet. */
export function boundaryRect(
  view: EditorView,
  index: number
): { top: number; left: number; width: number } | null {
  const all = topBlocks(view.state);
  if (!all.length) return null;
  const at = Math.max(0, Math.min(index, all.length));
  const ref = at < all.length ? all[at] : all[all.length - 1];
  const dom = view.nodeDOM(ref.pos) as HTMLElement | null;
  const rect = dom?.getBoundingClientRect?.();
  if (!rect) return null;
  return {
    top: at < all.length ? rect.top : rect.bottom,
    left: rect.left,
    width: rect.width,
  };
}

/* ─── The paint ─── */

/** What the host knows about an insertion that the document alone cannot
 *  say — whether the library is ahead — plus the edit state it has already
 *  derived for the panel. Handed to the paint by `setMeta(moduleStampKey, …)`
 *  from `useModuleInsert`, so the tag and the "In this document" row can
 *  never disagree. Absent ⇒ the tag shows name and version only. */
export interface InsertionTagInfo {
  moduleVersion: number;
  state: 'intact' | 'modified' | 'unknown';
  update: 'none' | 'available' | 'dismissed';
}

interface StampPaintState {
  deco: DecorationSet;
  info: Record<string, InsertionTagInfo>;
}

export const moduleStampKey = new PluginKey<StampPaintState>('moduleStampPaint');

/** Colour slots the band can take — one per existing semantic token
 *  (editor.css `.sf-mod-c0…c3`). */
export const INSERTION_COLOR_SLOTS = 4;

/** The tag's state line — icon + text, never colour alone (DESIGN §7). The
 *  same words the "In this document" badges use. */
function tagStateText(info: InsertionTagInfo | undefined): string[] {
  if (!info) return [];
  const out: string[] = [];
  if (info.state === 'modified') out.push('● Modified here');
  if (info.update === 'available') out.push('↻ Update available');
  return out;
}

/** The name tag at the top of one contiguous run of one insertion. EDITOR
 *  CHROME ONLY: a widget decoration is never part of the document, so it is
 *  never saved (`docToBlocks` reads the doc), never exported (the .docx/PDF
 *  engines read blocks), never copied (ProseMirror serialises the doc slice,
 *  not the DOM) and is hidden for print (editor.css). It carries no
 *  `data-block-id`, so every geometry walker that reads block rows
 *  (BlockGutter, `boundaryIndexAtCoords`) steps over it. */
function tagWidget(
  insertId: string,
  runStartId: string,
  slot: number,
  name: string,
  version: number,
  states: string[]
): HTMLElement {
  const el = document.createElement('div');
  el.className = `sf-mod-tag sf-mod-c${slot}`;
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('data-sf-mod-tag', insertId);
  el.setAttribute('data-sf-mod-run', runStartId);
  const label = `${name} · v${version}`;
  const title = `${label}${states.length ? ` — ${states.join(', ')}` : ''}. Click to select this piece and show it in the Modules panel.`;
  el.title = title;
  el.setAttribute('aria-label', title);
  // The tag itself is a zero-height anchor in the flow (editor.css): the
  // visible chip hangs off it, so the name never pushes a line of text.
  const chip = document.createElement('span');
  chip.className = 'sf-mod-tag-chip';
  const swatch = document.createElement('span');
  swatch.className = 'sf-mod-tag-swatch';
  swatch.setAttribute('aria-hidden', 'true');
  const text = document.createElement('span');
  text.className = 'sf-mod-tag-name';
  text.textContent = label;
  chip.append(swatch, text);
  for (const s of states) {
    const st = document.createElement('span');
    st.className = 'sf-mod-tag-state';
    st.textContent = s;
    chip.append(st);
  }
  el.append(chip);
  return el;
}

/** The chip shows while the pointer is over its piece (any block of the
 *  insertion, or the chip itself) — `is-hot` on the tag, set by the plugin's
 *  DOM handlers below. The rail and tint are the always-on cue; the name is
 *  on demand (Josh 2026-10-09: the banner broke the lines of text). */
function insertIdUnderPointer(target: EventTarget | null): string | null {
  const t = target instanceof Element ? target : null;
  if (!t) return null;
  const tag = t.closest<HTMLElement>('[data-sf-mod-tag]');
  if (tag) return tag.getAttribute('data-sf-mod-tag');
  const blk = t.closest<HTMLElement>('[data-module-insert]');
  return blk ? blk.getAttribute('data-module-insert') : null;
}

function setHotInsertion(root: HTMLElement, insertId: string | null): void {
  const current = root.getAttribute('data-sf-mod-hot') || null;
  if (current === insertId) return;
  if (insertId) root.setAttribute('data-sf-mod-hot', insertId);
  else root.removeAttribute('data-sf-mod-hot');
  root
    .querySelectorAll<HTMLElement>('.sf-mod-tag.is-hot')
    .forEach((n) => n.classList.remove('is-hot'));
  if (insertId) {
    root
      .querySelectorAll<HTMLElement>('.sf-mod-tag[data-sf-mod-tag="' + CSS.escape(insertId) + '"]')
      .forEach((n) => n.classList.add('is-hot'));
  }
}

/**
 * Blocks that arrived by insertion carry a left band and a whisper of tint,
 * capped at the top and bottom of each contiguous run so a run reads as ONE
 * thing — and, since modules-polish item 4, each INSERTION gets its own band
 * colour (`insertionColorSlots`: stable per insertion, never shared by two
 * touching insertions) plus a name tag at the top of every run, so two
 * insertions side by side never read as one.
 */
function build(state: EditorState, info: Record<string, InsertionTagInfo>): DecorationSet {
  const all = topBlocks(state);
  const decorations: Decoration[] = [];
  const slots = insertionColorSlots(
    all.map((b) => b.stamp?.insertId || null),
    INSERTION_COLOR_SLOTS
  );
  all.forEach((b, i) => {
    const id = b.stamp?.insertId;
    if (!id) return;
    const slot = slots.get(id) ?? 0;
    const startsRun = i === 0 || all[i - 1].stamp?.insertId !== id;
    const endsRun = i === all.length - 1 || all[i + 1].stamp?.insertId !== id;
    if (startsRun) {
      const known = info[id];
      const name = b.stamp?.moduleName || 'Module';
      const version = known?.moduleVersion ?? b.stamp?.moduleVersion ?? 1;
      const states = tagStateText(known);
      decorations.push(
        Decoration.widget(b.pos, () => tagWidget(id, b.id, slot, name, version, states), {
          // Before every other widget at this position (a numbered table's
          // own number sits at side -1).
          side: -2,
          ignoreSelection: true,
          stopEvent: () => true,
          key: `sf-mod-tag-${id}-${b.id}-${slot}-${version}-${name}-${states.join('|')}`,
        })
      );
    }
    decorations.push(
      Decoration.node(b.pos, b.pos + b.node.nodeSize, {
        class: `sf-mod-blk sf-mod-c${slot}${startsRun ? ' is-run-start' : ''}${endsRun ? ' is-run-end' : ''}`,
        'data-module-insert': id,
      })
    );
  });
  return decorations.length ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty;
}

/** The plugin on its own, so a test can build a bare EditorState with it. */
export function moduleStampPlugin(): Plugin<StampPaintState> {
  return new Plugin<StampPaintState>({
    key: moduleStampKey,
    state: {
      init: (_config, state) => ({ deco: build(state, {}), info: {} }),
      // Positions move with every edit and the stamps themselves only change
      // when the document does, so one rebuild per doc change (or per new tag
      // info from the host) is both correct and the least work possible.
      apply: (tr, value, _old, newState) => {
        const meta = tr.getMeta(moduleStampKey) as
          | { info: Record<string, InsertionTagInfo> }
          | undefined;
        if (!tr.docChanged && !meta) return value;
        const info = meta?.info ?? value.info;
        return { deco: build(newState, info), info };
      },
    },
    props: {
      decorations: (state) => moduleStampKey.getState(state)?.deco ?? DecorationSet.empty,
      handleDOMEvents: {
        mouseover: (view, event) => {
          setHotInsertion(view.dom, insertIdUnderPointer(event.target));
          return false;
        },
        mouseleave: (view) => {
          setHotInsertion(view.dom, null);
          return false;
        },
      },
    },
  });
}

/** Wired into BOTH canvases: an inserted block looks the same everywhere. */
export const ModuleStamps = Extension.create({
  name: 'moduleStamps',
  addProseMirrorPlugins() {
    return [moduleStampPlugin()];
  },
});
